import { useEffect, useState } from 'react'
import { getTheatricalRelease, TheatricalReleaseResult } from 'src/services/Theatrical'
import { TicketsLayout } from '@/layouts/TicketsLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getProjectGuildScore } from '@/services/ProjectsService/ProjectsService'
import { getUserAgent } from '@/services/UserAgentService'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getRegionFromParams } from '@/utils/nextUtils/paramsUtils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { loadTranslations } from '@/utils/translate/translate-server'
import { FreeTicketClaimPartnerView } from '@/views/TicketCheckoutViews/FreeTicketClaimPartnerView'
import { ShowtimesView } from '@/views/TicketCheckoutViews/ShowtimesView'
import { InitializedReservation, initReservation } from '@/views/TicketCheckoutViews/ShowtimesView/initReservation'

interface TicketsPageProps {
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  region: string
  projectGuildScore: number | null
  reservation: InitializedReservation
}

export const TicketsRegionPage: NextPageWithLayout<TicketsPageProps> = ({
  theatricalSlug,
  theatricalRelease,
  projectGuildScore,
  reservation,
}) => {
  const theatricalReleaseRegion = theatricalRelease?.region

  const isTicketClaimRegion = theatricalReleaseRegion?.useSimpleWaitlistOverlay

  const ticketingProviderSlugForRegion = theatricalReleaseRegion?.defaultTicketingProviderSlug

  const { query } = useSafeRouter()
  const freeTicketClaimQuery = query?.promo === 'claim-free-ticket'
  const [showTicketClaim, setShowTicketClaim] = useState(isTicketClaimRegion && freeTicketClaimQuery)

  useEffect(() => {
    setShowTicketClaim(isTicketClaimRegion && freeTicketClaimQuery)
  }, [isTicketClaimRegion, freeTicketClaimQuery])

  const handleOnClaimTicketClick = () => {
    setShowTicketClaim(false)
    if (ticketingProviderSlugForRegion) {
      const queryParams = new URLSearchParams(window.location.search)
      queryParams.set('ticketUrl', ticketingProviderSlugForRegion)
      queryParams.set('selectedVenue', ticketingProviderSlugForRegion)
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`
      window.history.replaceState(null, '', newUrl)
    }
  }

  if (showTicketClaim)
    return <FreeTicketClaimPartnerView onClick={handleOnClaimTicketClick} theatricalRelease={theatricalRelease} />

  return (
    <TicketsLayout
      shouldShowLanguageModal
      isDarkMode={true}
      shouldRemoveNavOnMobile={false}
      useDefaultNav
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
    >
      <ShowtimesView
        theatricalRelease={theatricalRelease}
        theatricalSlug={theatricalSlug}
        projectGuildScore={projectGuildScore}
        reservation={reservation}
      />
    </TicketsLayout>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<TicketsPageProps>(
  async ({ federationClient, locale, next404, params, query, anonymousId, req }) => {
    const region = getRegionFromParams(params)
    const theatricalSlug = getItemOrFirstEntry(params.slug)
    if (!theatricalSlug) {
      return next404()
    }

    // Skip reservation creation for bots to prevent unnecessary reservations
    const userAgentHeader = req.headers['user-agent']
    const { isBot } = getUserAgent(userAgentHeader)

    const [reservation, theatricalRelease, projectGuildScore, translations] = await Promise.all([
      isBot ? Promise.resolve(null) : initReservation({ query, federationClient, theatricalSlug, anonymousId }),
      getTheatricalRelease({ theatricalSlug, client: federationClient }),
      getProjectGuildScore(theatricalSlug, federationClient),
      loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account', 'theatrical-presales']),
    ])

    if (!theatricalRelease) {
      return next404()
    }

    return {
      props: {
        theatricalSlug,
        theatricalRelease,
        region,
        reservation,
        projectGuildScore: projectGuildScore?.guildScore ?? null,
        ...translations,
      },
    }
  },
)

TicketsRegionPage.getLayout = (page) => {
  return page
}
export default TicketsRegionPage
